import ProtectedRoute from 'authorization-module/lib/ui/atoms/ProtectedRoute'
import config from 'const/config'
import routes from 'const/routes'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import AccountsDashboard from 'pages/AccountsDashboard'
import { FullPageRewardsError } from 'pages/AccountsDashboard/TechnicalError/subcomponents'
import Callback from 'pages/Callback'
import ErrorPage from 'pages/ErrorPage'
import PageLayout from 'pages/PageLayout'
import PageNotFound from 'pages/PageNotFound'
import { lazy } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import isMockData from 'syf-js-utilities/helpers/isMockData'
import LWSError from 'pages/LWSError'
import { useAtomValue } from 'jotai'
import { selectedAccountAtom } from 'store/atoms'

import FullPageRoute from './FullPageRoute'
import SuspenseRoute from './SuspenseRoute'
import { OutletWrapperHOC, RouteWrapper } from './subcomponents'
import CreateOrChangePinModal from './CreateOrChangePin'
import CardActivationModal from './CardActivation'
import DetailsModal from './DetailsModal'

// Lazy load components for better performance and code splitting.
const AccountServices = lazy(() => import('pages/AccountServices'))
const Activity = lazy(() => import('pages/Activity'))
const Alerts = lazy(() => import('pages/Alerts'))

const CreditBalanceRefund = lazy(() => import('pages/CreditBalanceRefund'))
const CreditView = lazy(() => import('pages/CreditView'))
const CreditViewError = lazy(() => import('pages/CreditView/CreditViewError'))
const DisputesCenter = lazy(() => import('pages/DisputesCenter'))
const DocumentCenter = lazy(() => import('pages/DocumentCenter'))
const Logout = lazy(() => import('pages/Logout'))
const MarketingAndPrivacy = lazy(() => import('pages/PreferenceSettings'))
const Profile = lazy(() => import('pages/Profile'))
const PromotionsPage = lazy(() => import('pages/Promotions'))
const Rewards = lazy(() => import('pages/Rewards'))
const Statements = lazy(() => import('pages/Statements'))
const VantageScore = lazy(() => import('pages/VantageScore'))

// Renders the Accounts Dashboard or a fallback component based on route conditions.
const RenderDashboardWithOutlet = () => {
  const selectedAccount = useAtomValue(selectedAccountAtom)

  const location = useLocation()
  if (location.pathname === routes.ACCOUNT_DETAILS && !selectedAccount) {
    return <PageNotFound />
  }
  return (
    <OutletWrapperHOC>
      <AccountsDashboard />
    </OutletWrapperHOC>
  )
}

const AppRoutes = ({
  authenticateCallback,
  authorizedCallback,
  setTokenFetched
}) => {
  const isUsingMockData = isMockData(config.APP_ENV)

  return (
    <RouteWrapper>
      <Routes>
        <Route
          path=""
          element={
            <ProtectedRoute
              authenticateCallback={authenticateCallback}
              authorizedCallback={authorizedCallback}
              isMockData={isUsingMockData}
            />
          }
        >
          <Route path="" element={<RenderDashboardWithOutlet />}>
            <Route path={routes.ACCOUNT_DETAILS} element={<DetailsModal />} />
          </Route>
        </Route>
        <Route
          path={routes.CALLBACK}
          element={<Callback setTokenFetched={setTokenFetched} />}
        />
        <Route path={routes.ERROR} element={<ErrorPage />} />
        <Route
          path={routes.ACCOUNT_SERVICES}
          element={
            <ProtectedRoute
              authenticateCallback={authenticateCallback}
              authorizedCallback={authorizedCallback}
              isMockData={isUsingMockData}
            />
          }
        >
          <Route element={<PageLayout />}>
            <Route path="" element={<AccountServices />}>
              {isFeatureFlagEnabled('isCreateOrChangePinEnabled') && (
                <Route
                  path={routes.CHANGE_PIN}
                  element={<CreateOrChangePinModal />}
                />
              )}
              {isFeatureFlagEnabled('isPostLoginActivationEnabled') && (
                <Route
                  path={routes.CARD_ACTIVATION}
                  element={<CardActivationModal />}
                />
              )}
            </Route>
          </Route>
        </Route>
        <Route element={<PageLayout />}>
          <Route path={routes.ACTIVITY} element={<Activity />} />
          <Route path={routes.DISPUTES} element={<DisputesCenter />} />
          <Route
            path={routes.PROMOTIONS}
            element={
              <FullPageRoute featureFlag>
                <PromotionsPage />
              </FullPageRoute>
            }
          />
          <Route
            path={routes.REWARDS}
            element={
              <FullPageRoute featureFlag>
                <Rewards />
              </FullPageRoute>
            }
          />
          <Route
            path={routes.STATEMENTS}
            element={
              <FullPageRoute featureFlag>
                <Statements />
              </FullPageRoute>
            }
          />
        </Route>
        <Route
          path={routes.LOGOUT}
          element={
            <SuspenseRoute>
              <Logout />
            </SuspenseRoute>
          }
        />
        <Route path={routes.REWARDS_ERROR} element={<FullPageRewardsError />} />
        <Route
          path={routes.CREDIT_BALANCE_REFUND}
          element={
            <SuspenseRoute>
              <CreditBalanceRefund />
            </SuspenseRoute>
          }
        />
        <Route
          path={routes.CREDIT_VIEW}
          element={
            <SuspenseRoute>
              <CreditView />
            </SuspenseRoute>
          }
        />
        <Route
          path={routes.CREDIT_VIEW_ERROR}
          element={
            <SuspenseRoute>
              <CreditViewError />
            </SuspenseRoute>
          }
        />
        <Route
          path={routes.PROFILE}
          element={
            <SuspenseRoute>
              <Profile />
            </SuspenseRoute>
          }
        />

        <Route
          path={routes.ALERTS}
          element={
            <SuspenseRoute>
              <Alerts />
            </SuspenseRoute>
          }
        />
        <Route path={routes.LWS_ERROR} element={<LWSError />} />
        <Route
          path={routes.MARKETING_PRIVACY}
          element={
            <SuspenseRoute>
              <MarketingAndPrivacy />
            </SuspenseRoute>
          }
        />
        <Route
          path={routes.CREDIT_SCORE}
          element={
            <SuspenseRoute>
              <VantageScore />
            </SuspenseRoute>
          }
        />
        {isFeatureFlagEnabled('isDocumentCenterEnabled') && (
          <Route
            path={routes.DOCUMENT_CENTER}
            element={
              <SuspenseRoute>
                <DocumentCenter />
              </SuspenseRoute>
            }
          />
        )}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </RouteWrapper>
  )
}

export default AppRoutes
